// ============================================================================
// EduPath Sprint 5 - useOfflineSync Hook
// ============================================================================
// React hook providing clean access to offline sync functionality.
// Handles initialization, toast auto-clear, and computed properties.
// ============================================================================

import { useEffect, useCallback, useMemo } from 'react';
import { useOfflineSyncStore } from '@/stores/offlineSyncStore';
import type { ConflictResolution } from '@/types/offline';

const TOAST_DURATION_MS = 5000;

export function useOfflineSync() {
  const store = useOfflineSyncStore();

  // Initialize sync manager on first mount
  useEffect(() => {
    store.initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-clear toast after duration
  useEffect(() => {
    if (store.toastMessage) {
      const timer = setTimeout(() => {
        store.clearToast();
      }, TOAST_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [store.toastMessage, store.clearToast]);

  // ── Computed Properties ──────────────────────────────────────────────────

  /** Whether there are any items needing attention (failed/rejected/conflict) */
  const hasIssues = useMemo(
    () =>
      store.syncStatus.failedCount > 0 ||
      store.syncStatus.rejectedCount > 0 ||
      store.syncStatus.conflictCount > 0,
    [store.syncStatus]
  );

  /** Whether sync is actively running */
  const isSyncing = store.syncStatus.state === 'SYNCING';

  /** Whether device is offline */
  const isOffline = !store.syncStatus.isOnline;

  /** Total items in queue (non-synced) */
  const pendingTotal =
    store.syncStatus.pendingCount +
    store.syncStatus.syncingCount +
    store.syncStatus.failedCount;

  /** Items that need user attention */
  const actionableItems = useMemo(
    () =>
      store.queueItems.filter(
        (item) =>
          item.status === 'REJECTED' ||
          item.status === 'CONFLICT' ||
          (item.status === 'FAILED' && item.retryCount >= item.maxRetries)
      ),
    [store.queueItems]
  );

  /** Items currently in progress */
  const activeItems = useMemo(
    () =>
      store.queueItems.filter(
        (item) =>
          item.status === 'PENDING' ||
          item.status === 'SYNCING' ||
          (item.status === 'FAILED' && item.retryCount < item.maxRetries)
      ),
    [store.queueItems]
  );

  // ── Stable Action References ─────────────────────────────────────────────

  const syncNow = useCallback(() => store.syncNow(), [store]);
  const retryItem = useCallback(
    (id: string) => store.retryItem(id),
    [store]
  );
  const discardItem = useCallback(
    (id: string) => store.discardItem(id),
    [store]
  );
  const resolveConflict = useCallback(
    (id: string, resolution: ConflictResolution) =>
      store.resolveConflict(id, resolution),
    [store]
  );
  const enqueueAssessment = useCallback(
    (params: Parameters<typeof store.enqueueAssessment>[0]) =>
      store.enqueueAssessment(params),
    [store]
  );

  return {
    // Status
    syncStatus: store.syncStatus,
    queueItems: store.queueItems,
    hasIssues,
    isSyncing,
    isOffline,
    pendingTotal,
    actionableItems,
    activeItems,

    // UI State
    toastMessage: store.toastMessage,
    conflictModalOpen: store.conflictModalOpen,
    conflictItem: store.conflictItem,

    // Actions
    syncNow,
    retryItem,
    discardItem,
    resolveConflict,
    enqueueAssessment,
    openConflictModal: store.openConflictModal,
    closeConflictModal: store.closeConflictModal,
    clearToast: store.clearToast,
    pauseSync: store.pauseSync,
    resumeSync: store.resumeSync,
    refresh: store.refresh,
  };
}

export default useOfflineSync;
